/**
 * 统一错误处理管理器
 * 整合全局错误处理、RTC错误处理和错误上报机制
 */

/* eslint-disable max-classes-per-file */
import { BaseManager, ManagerState } from './BaseManager';
import logger from '@/utils/logger';

// 错误级别枚举
export enum ErrorLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

// 错误类型枚举
export enum ErrorType {
  JAVASCRIPT = 'javascript',
  PROMISE = 'promise',
  RESOURCE = 'resource',
  RTC = 'rtc',
  NETWORK = 'network',
  BUSINESS = 'business',
  UNKNOWN = 'unknown',
}

// 错误信息接口
export interface ErrorInfo {
  id: string;
  type: ErrorType;
  level: ErrorLevel;
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
  timestamp: number;
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
  context?: Record<string, any>;
  handled?: boolean;
}

// 错误处理器接口
export interface ErrorHandler {
  canHandle(error: any, context?: any): boolean;
  handle(error: any, context?: any): ErrorInfo | null;
  priority: number; // 优先级，数字越大优先级越高
}

// 错误上报器接口
export interface ErrorReporter {
  report(errorInfo: ErrorInfo): Promise<void>;
  batchReport?(errorInfos: ErrorInfo[]): Promise<void>;
}

// 错误过滤器接口
export interface ErrorFilter {
  shouldIgnore(errorInfo: ErrorInfo): boolean;
}

/**
 * JavaScript错误处理器
 */
class JavaScriptErrorHandler implements ErrorHandler {
  priority = 100;

  canHandle(error: any): boolean {
    return error instanceof Error || (typeof error === 'object' && error !== null && error.message);
  }

  handle(error: any, context?: any): ErrorInfo {
    const errorInfo: ErrorInfo = {
      id: this.generateErrorId(),
      type: ErrorType.JAVASCRIPT,
      level: ErrorLevel.ERROR,
      message: error.message || String(error),
      stack: error.stack,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context,
      handled: false,
    };

    // 从错误事件中提取更多信息
    if (context && typeof context === 'object') {
      if (context.filename) errorInfo.source = context.filename;
      if (context.lineno) errorInfo.line = context.lineno;
      if (context.colno) errorInfo.column = context.colno;
    }

    return errorInfo;
  }

  private generateErrorId(): string {
    return `js_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Promise错误处理器
 */
class PromiseErrorHandler implements ErrorHandler {
  priority = 90;

  canHandle(error: any, context?: any): boolean {
    return context && context.type === 'unhandledrejection';
  }

  handle(error: any, context?: any): ErrorInfo {
    const reason = error.reason || error;

    return {
      id: `promise_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: ErrorType.PROMISE,
      level: ErrorLevel.ERROR,
      message: reason.message || String(reason),
      stack: reason.stack,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context: { ...context, originalError: error },
      handled: false,
    };
  }
}

/**
 * 资源加载错误处理器
 */
class ResourceErrorHandler implements ErrorHandler {
  priority = 80;

  canHandle(error: any, context?: any): boolean {
    return (
      context &&
      context.type === 'error' &&
      context.target &&
      (context.target.tagName === 'IMG' ||
        context.target.tagName === 'SCRIPT' ||
        context.target.tagName === 'LINK')
    );
  }

  handle(error: any, context?: any): ErrorInfo {
    const target = context.target;

    return {
      id: `resource_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: ErrorType.RESOURCE,
      level: ErrorLevel.WARN,
      message: `资源加载失败: ${target.src || target.href}`,
      source: target.src || target.href,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context: {
        tagName: target.tagName,
        src: target.src,
        href: target.href,
      },
      handled: false,
    };
  }
}

/**
 * RTC错误处理器
 */
class RTCErrorHandler implements ErrorHandler {
  priority = 110;

  canHandle(error: any, context?: any): boolean {
    return (
      (context && context.source === 'rtc') ||
      (error && error.message && error.message.includes('RTC'))
    );
  }

  handle(error: any, context?: any): ErrorInfo {
    return {
      id: `rtc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: ErrorType.RTC,
      level: this.determineRTCErrorLevel(error),
      message: error.message || String(error),
      stack: error.stack,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context: { ...context, rtcError: true },
      handled: false,
    };
  }

  private determineRTCErrorLevel(error: any): ErrorLevel {
    const message = error.message || String(error);

    if (message.includes('网络') || message.includes('连接')) {
      return ErrorLevel.WARN;
    }

    if (message.includes('权限') || message.includes('设备')) {
      return ErrorLevel.ERROR;
    }

    return ErrorLevel.ERROR;
  }
}

/**
 * 默认错误过滤器
 */
class DefaultErrorFilter implements ErrorFilter {
  private ignoredMessages = new Set([
    'Script error.',
    'Non-Error promise rejection captured',
    'ResizeObserver loop limit exceeded',
  ]);

  private ignoredSources = new Set([
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
  ]);

  shouldIgnore(errorInfo: ErrorInfo): boolean {
    // 忽略特定消息
    if (this.ignoredMessages.has(errorInfo.message)) {
      return true;
    }

    // 忽略浏览器扩展错误
    if (
      errorInfo.source &&
      Array.from(this.ignoredSources).some((prefix) => errorInfo.source!.startsWith(prefix))
    ) {
      return true;
    }

    // 忽略过于频繁的相同错误
    return false;
  }
}

/**
 * 控制台错误上报器
 */
class ConsoleErrorReporter implements ErrorReporter {
  async report(errorInfo: ErrorInfo): Promise<void> {
    const logMethod = this.getLogMethod(errorInfo.level);
    logMethod(`[ErrorManager] ${errorInfo.type}:`, errorInfo);
  }

  private getLogMethod(level: ErrorLevel) {
    switch (level) {
      case ErrorLevel.DEBUG:
        return process.env.NODE_ENV === 'development' ? console.debug : () => {};
      case ErrorLevel.INFO:
        return process.env.NODE_ENV === 'development' ? console.info : () => {};
      case ErrorLevel.WARN:
        return console.warn;
      case ErrorLevel.ERROR:
        return console.error;
      case ErrorLevel.FATAL:
        return console.error;
      default:
        return console.error;
    }
  }
}

/**
 * 远程错误上报器
 */
class RemoteErrorReporter implements ErrorReporter {
  private reportUrl: string;

  private batchSize: number = 10;

  private batchTimeout: number = 5000;

  private pendingErrors: ErrorInfo[] = [];

  private batchTimer?: NodeJS.Timeout;

  constructor(reportUrl: string) {
    this.reportUrl = reportUrl;
  }

  async report(errorInfo: ErrorInfo): Promise<void> {
    this.pendingErrors.push(errorInfo);

    if (this.pendingErrors.length >= this.batchSize) {
      await this.flushBatch();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.flushBatch();
      }, this.batchTimeout);
    }
  }

  async batchReport(errorInfos: ErrorInfo[]): Promise<void> {
    try {
      const response = await fetch(this.reportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ errors: errorInfos }),
      });

      if (!response.ok) {
        throw new Error(`上报失败: ${response.status}`);
      }

      logger.debug(`[ErrorManager] 成功上报 ${errorInfos.length} 个错误`);
    } catch (error) {
      logger.warn('[ErrorManager] 错误上报失败:', error);
    }
  }

  private async flushBatch(): Promise<void> {
    if (this.pendingErrors.length === 0) return;

    const errors = this.pendingErrors.splice(0);

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }

    await this.batchReport(errors);
  }
}

/**
 * 统一错误管理器
 */
export class ErrorManager extends BaseManager {
  private handlers: ErrorHandler[] = [];

  private reporters: ErrorReporter[] = [];

  private filters: ErrorFilter[] = [];

  // 简化的错误计数
  private errorCount = 0;

  private isInitialized = false;

  constructor() {
    super({
      name: 'error-manager',
      autoInit: false, // 禁用自动初始化，手动控制初始化时机
    });

    // 确保属性初始化完成后再初始化
    this.init().catch((error) => {
      logger.error(`[ErrorManager] 手动初始化失败: ${this.config.name}`, error);
      this.state = ManagerState.ERROR;
    });
  }

  protected async onInit(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('[ErrorManager] 已经初始化过了');
      return;
    }

    // 注册默认处理器
    this.addHandler(new JavaScriptErrorHandler());
    this.addHandler(new PromiseErrorHandler());
    this.addHandler(new ResourceErrorHandler());
    this.addHandler(new RTCErrorHandler());

    // 注册默认过滤器
    this.addFilter(new DefaultErrorFilter());

    // 注册默认上报器
    this.addReporter(new ConsoleErrorReporter());

    // 设置全局错误监听
    this.setupGlobalErrorHandlers();

    this.isInitialized = true;
    logger.info('[ErrorManager] 错误管理器初始化完成');
  }

  protected async onDestroy(): Promise<void> {
    this.removeGlobalErrorHandlers();
    this.handlers.length = 0;
    this.reporters.length = 0;
    this.filters.length = 0;
    this.isInitialized = false;
    logger.info('[ErrorManager] 错误管理器已销毁');
  }

  /**
   * 添加错误处理器
   */
  addHandler(handler: ErrorHandler): void {
    this.handlers.push(handler);
    this.handlers.sort((a, b) => b.priority - a.priority);
    logger.debug(`[ErrorManager] 添加错误处理器，优先级: ${handler.priority}`);
  }

  /**
   * 添加错误上报器
   */
  addReporter(reporter: ErrorReporter): void {
    this.reporters.push(reporter);
    logger.debug('[ErrorManager] 添加错误上报器');
  }

  /**
   * 添加错误过滤器
   */
  addFilter(filter: ErrorFilter): void {
    this.filters.push(filter);
    logger.debug('[ErrorManager] 添加错误过滤器');
  }

  /**
   * 添加远程上报器
   */
  addRemoteReporter(reportUrl: string): void {
    this.addReporter(new RemoteErrorReporter(reportUrl));
  }

  /**
   * 手动处理错误
   */
  async handleError(error: any, context?: any): Promise<void> {
    const errorInfo = this.processError(error, context);
    if (errorInfo) {
      await this.reportError(errorInfo);
      this.errorCount++;
    }
  }

  /**
   * 处理错误
   */
  private processError(error: any, context?: any): ErrorInfo | null {
    // 查找合适的处理器
    const handler = this.handlers.find((h) => h.canHandle(error, context));

    if (!handler) {
      logger.warn('[ErrorManager] 没有找到合适的错误处理器:', error);
      return null;
    }

    const errorInfo = handler.handle(error, context);
    if (!errorInfo) {
      return null;
    }

    // 应用过滤器
    const shouldIgnore = this.filters.some((filter) => filter.shouldIgnore(errorInfo));
    if (shouldIgnore) {
      logger.debug('[ErrorManager] 错误被过滤器忽略:', errorInfo.message);
      return null;
    }

    return errorInfo;
  }

  /**
   * 上报错误
   */
  private async reportError(errorInfo: ErrorInfo): Promise<void> {
    const reportPromises = this.reporters.map((reporter) =>
      reporter
        .report(errorInfo)
        .catch((error) => logger.warn('[ErrorManager] 错误上报失败:', error))
    );

    await Promise.all(reportPromises);
  }

  /**
   * 设置全局错误处理
   */
  private setupGlobalErrorHandlers(): void {
    // JavaScript错误
    window.addEventListener('error', this.handleWindowError);

    // Promise错误
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  /**
   * 移除全局错误处理
   */
  private removeGlobalErrorHandlers(): void {
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  /**
   * 处理window错误事件
   */
  private handleWindowError = (event: ErrorEvent): void => {
    this.handleError(event.error || new Error(event.message), {
      type: 'error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      target: event.target,
    });
  };

  /**
   * 处理未捕获的Promise错误
   */
  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    this.handleError(event, {
      type: 'unhandledrejection',
      reason: event.reason,
    });
  };

  /**
   * 获取简化的错误统计
   */
  getErrorCount(): number {
    return this.errorCount;
  }

  /**
   * 重置错误计数
   */
  resetErrorCount(): void {
    this.errorCount = 0;
    logger.debug('[ErrorManager] 错误计数已重置');
  }

  /**
   * 触发测试错误
   */
  triggerTestError(): void {
    logger.info('[ErrorManager] 触发测试错误');
    throw new Error('这是一个测试错误');
  }

  /**
   * 触发测试Promise错误
   */
  triggerTestPromiseError(): void {
    logger.info('[ErrorManager] 触发测试Promise错误');
    Promise.reject(new Error('这是一个测试Promise错误'));
  }
}

// 导出单例实例
export const errorManager = new ErrorManager();

// 导出便捷函数
export const handleError = (error: any, context?: any) => errorManager.handleError(error, context);

export const getErrorCount = () => errorManager.getErrorCount();

export const addErrorReporter = (reporter: ErrorReporter) => errorManager.addReporter(reporter);

export const addRemoteErrorReporter = (reportUrl: string) =>
  errorManager.addRemoteReporter(reportUrl);
